import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { TreeSelect, message } from 'antd';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  InfoCircleIcon,
  DragHandleIcon,
  EscalateArrowIcon,
} from '../../assets/icons';
import AddDesignationModal, {
  DESIGNATION_TYPE_TONES,
  type ControlMode,
  type DesignationType,
  type NewDesignation,
} from './createDepartment/AddDesignationModal';
import {
  useGetDepartmentQuery,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  type ApiDesignation,
} from '../../features/departments/departmentsApi';
import { useGetLocationHierarchiesQuery, type EcTreeNode } from '../../features/territory/territoryApi';
import { useActiveOrgId } from '../../features/organizations/useActiveOrg';

/* ------------------------------------------------------------------ *
 * Create Government Department (Build Hierarchy → Dept. Configuration)
 * Figma 1691-1005. Register a department + configure its designation ranks.
 * ------------------------------------------------------------------ */

interface Designation extends NewDesignation {
  id: string;
}

/** Department-level control mode (kept in state; not surfaced in this design). */
type DeptControlMode = 'direct' | 'dual' | 'autonomous';

/** antd TreeSelect node built from the org's location tree. */
interface TerritoryTreeNode {
  /** Full ancestor path ("a → b → c") — also the stored value + selected label. */
  value: string;
  /** Node name shown in the tree rows. */
  title: string;
  /** Full path shown as the selected value (treeNodeLabelProp). */
  fullTitle: string;
  children?: TerritoryTreeNode[];
}

/** Territory hierarchies a department can be mapped to (demo list). */
const TERRITORY_HIERARCHIES = [
  'State Revenue (General Administration)',
  'State Police',
  'Panchayati Raj',
  'Urban Local Bodies',
];

/** Core post name for escalation labels — drops the "(…)" / "/ …" qualifiers. */
const baseName = (name: string) => name.split('(')[0].split('/')[0].trim();

/** Identity-card fields managed by Formik (designations live in their own state). */
interface DeptForm {
  name: string;
  territory: string[];
  controlMode: DeptControlMode;
  active: boolean;
  description: string;
}

const deptValidationSchema = Yup.object({
  name: Yup.string().trim().required('Department name is required'),
});

/** Shared grid template so the header and body rows stay aligned. */
const GRID =
  'grid grid-cols-[90px_minmax(200px,1.3fr)_140px_140px_minmax(170px,1.1fr)_90px] items-center';

/** Design-matched field styling (slate-300 border, rounded, focus ring). */
const INPUT =
  'w-full rounded-lg border border-[#cbd5e1] bg-white px-[15px] py-[11px] text-sm text-ink placeholder:text-[#94a3b8] transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="text-xs font-semibold leading-4 text-[#334155]">
      {children}
      {required && <span className="font-semibold text-[#ef4444]"> *</span>}
    </label>
  );
}

/** Active / Inactive segmented pill. */
function StatusToggle({ active, onChange }: { active: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-[#64748b]">Status:</span>
      <div className="flex items-center gap-[1px] rounded-lg border border-brand-shade bg-brand-shade p-[3px]">
        {[
          { label: 'Active', value: true },
          { label: 'Inactive', value: false },
        ].map(({ label, value }) => (
          <button
            key={label}
            type="button"
            onClick={() => onChange(value)}
            className={[
              'rounded-md px-3 py-1 text-xs transition-colors',
              active === value
                ? 'bg-white font-semibold text-brand shadow-card'
                : 'font-medium text-[#46556f] hover:text-brand',
            ].join(' ')}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** One designation row in the hierarchy table. */
function DesignationRow({
  row,
  escalatesTo,
  onEdit,
  onDelete,
}: {
  row: Designation;
  /** Derived senior-post label ("<name> (Rank N)"), or null for the apex post. */
  escalatesTo: string | null;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className={[GRID, 'border-t border-line-soft', row.terminal ? 'bg-brand-shade/20' : 'bg-white'].join(' ')}>
      {/* Rank — drag handle + badge */}
      <div className="flex items-center gap-1.5 px-4 py-3">
        <DragHandleIcon className="size-3.5 shrink-0 text-[#94a3b8]" />
        <span className="flex size-6 items-center justify-center rounded-md border border-[#d1e2ff] bg-brand-shade text-xs font-bold text-brand">
          {row.rank}
        </span>
      </div>

      {/* Designation name */}
      <div className="px-4 py-3 pl-8">
        <p className="text-xs font-semibold leading-4 text-ink">{row.name}</p>
      </div>

      {/* Authority */}
      <div className="flex justify-center px-4 py-3">
        <span
          className={[
            'inline-flex items-center rounded-full border px-[11px] py-[3px] text-[11px] font-medium',
            DESIGNATION_TYPE_TONES[row.type],
          ].join(' ')}
        >
          {row.type}
        </span>
      </div>

      {/* Lowest level? */}
      <div className="flex justify-center px-4 py-3">
        {row.terminal ? (
          <span className="rounded bg-[#d1fae5] px-2 py-0.5 text-center text-[11px] font-semibold text-[#065f46]">
            Yes - Terminal Post
          </span>
        ) : (
          <span className="text-xs font-medium text-[#94a3b8]">No</span>
        )}
      </div>

      {/* Escalates to */}
      <div className="px-4 py-3">
        {escalatesTo ? (
          <span className="flex items-center gap-1">
            <EscalateArrowIcon className="shrink-0 text-brand" />
            <span className="text-xs text-ink">{escalatesTo}</span>
          </span>
        ) : (
          <span className="text-xs italic text-[#64748b]">— (Apex Post)</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-1 px-4 py-3">
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${row.name}`}
          className="rounded p-1 text-[#94a3b8] transition-colors hover:bg-brand-shade hover:text-brand"
        >
          <PencilIcon className="size-4" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Remove ${row.name}`}
          className="rounded p-1 text-[#94a3b8] transition-colors hover:bg-red-50 hover:text-red-500"
        >
          <TrashIcon className="size-4" />
        </button>
      </div>
    </div>
  );
}

export default function CreateGovernmentDepartmentPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { data: editDept } = useGetDepartmentQuery(id!, { skip: !isEdit });
  // Territory Hierarchy options come from the org's location hierarchies
  // (GET /territory/location-hierarchies/?organization=<id>).
  const activeOrgId = useActiveOrgId();
  const territoryOrg = editDept?.organization ?? activeOrgId;
  const { data: locationTree, isFetching: territoryLoading } = useGetLocationHierarchiesQuery(
    { organization: territoryOrg as number },
    { skip: territoryOrg == null },
  );
  const [createDepartment, { isLoading: creating }] = useCreateDepartmentMutation();
  const [updateDepartment, { isLoading: updating }] = useUpdateDepartmentMutation();
  const saving = creating || updating;

  const [designations, setDesignations] = useState<Designation[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  // Id of the designation being edited (null = adding a new one).
  const [editingId, setEditingId] = useState<string | null>(null);

  // Display order is driven by rank (ascending); escalation always points one rank up.
  const sorted = [...designations].sort((a, b) => a.rank - b.rank);
  const nextRank = sorted.length ? sorted[sorted.length - 1].rank + 1 : 1;
  const editingRow = editingId ? designations.find((d) => d.id === editingId) ?? null : null;

  const openAddModal = () => {
    setEditingId(null);
    setModalOpen(true);
  };
  const openEditModal = (rowId: string) => {
    setEditingId(rowId);
    setModalOpen(true);
  };

  // Add a new designation, or save edits to the one currently being edited. Control modes are
  // exclusive — only one designation may be Primary and one Secondary, so assigning either to a
  // row demotes any previous holder to None.
  const submitDesignation = (d: NewDesignation) => {
    setDesignations((rows) => {
      const rowId = editingId ?? `d${Date.now()}`;
      const upserted = editingId
        ? rows.map((r) => (r.id === editingId ? { ...d, id: editingId } : r))
        : [...rows, { ...d, id: rowId }];
      if (d.controlMode === 'Primary' || d.controlMode === 'Secondary') {
        return upserted.map((r) =>
          r.id !== rowId && r.controlMode === d.controlMode
            ? { ...r, controlMode: 'None' as ControlMode }
            : r,
        );
      }
      return upserted;
    });
    setModalOpen(false);
    setEditingId(null);
  };

  const removeDesignation = (rowId: string) =>
    setDesignations((rows) => rows.filter((r) => r.id !== rowId));

  const formik = useFormik<DeptForm>({
    initialValues: { name: '', territory: [], controlMode: 'dual', active: true, description: '' },
    validationSchema: deptValidationSchema,
    onSubmit: async (values) => {
      const payload = {
        name: values.name.trim(),
        description: values.description.trim() || undefined,
        territory_hierarchy: values.territory.length ? values.territory.join(', ') : undefined,
        control_mode: values.controlMode,
        status: (values.active ? 'Active' : 'Inactive') as 'Active' | 'Inactive',
        designations: sorted.map<ApiDesignation>((d) => ({
          name: d.name,
          rank: d.rank,
          is_lowest_level: d.terminal,
          control_mode: d.controlMode === 'None' ? null : d.controlMode,
          designation_type: d.type,
        })),
      };
      try {
        if (isEdit && id) {
          await updateDepartment({ id, patch: payload }).unwrap();
          message.success('Department updated.');
        } else {
          await createDepartment(payload).unwrap();
          message.success('Department created.');
        }
        navigate('/admin/dept-configuration');
      } catch {
        message.error('Could not save the department. Please try again.');
      }
    },
  });
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formik;
  const nameError = touched.name && errors.name ? errors.name : undefined;

  // Edit mode — prefill once the department loads.
  useEffect(() => {
    if (!editDept) return;
    formik.setValues({
      name: editDept.name ?? '',
      territory: editDept.territory_hierarchy
        ? editDept.territory_hierarchy.split(',').map((t) => t.trim()).filter(Boolean)
        : [],
      controlMode: editDept.control_mode ?? 'dual',
      active: editDept.active ?? editDept.status === 'Active',
      description: editDept.description ?? '',
    });
    setDesignations(
      (editDept.designations ?? []).map((d, i) => ({
        id: `d${d.id ?? i}`,
        name: d.name,
        rank: d.rank,
        controlMode: (d.control_mode as ControlMode) || 'None',
        type: (d.designation_type as DesignationType) || 'Official',
        terminal: d.is_lowest_level,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDept]);

  // The location tree rendered as antd TreeSelect data. Each node's value + selected label is
  // its full ancestor path ("a → b → c"); the tree rows show just the node name.
  const territoryTreeData = useMemo<TerritoryTreeNode[]>(() => {
    const toNodes = (nodes: EcTreeNode[], ancestors: string[]): TerritoryTreeNode[] =>
      nodes
        .filter((n) => n.name)
        .map((n) => {
          const path = [...ancestors, n.name as string];
          const fullTitle = path.join(' → ');
          const kids = n.children ? toNodes(n.children, path) : [];
          return {
            value: fullTitle,
            title: n.name as string,
            fullTitle,
            ...(kids.length ? { children: kids } : {}),
          };
        });
    const built = toNodes(locationTree ?? [], []);
    return built.length
      ? built
      : TERRITORY_HIERARCHIES.map((t) => ({ value: t, title: t, fullTitle: t }));
  }, [locationTree]);

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[
            { label: 'Government Dep.', to: '/admin/dept-configuration' },
            { label: isEdit ? 'Edit' : 'Create New' },
          ]}
        />
        <h1 className="pt-1 text-2xl font-bold leading-8 tracking-[-0.6px] text-[#0f172a]">
          {isEdit ? 'Edit Government Department' : 'Create Government Department'}
        </h1>
        <p className="text-sm leading-5 text-[#64748b]">
          Register a new government department and configure its internal hierarchy and designations.
        </p>
      </div>

      {/* Section: Department Identity */}
      <section className="flex flex-col gap-6 rounded-xl border border-[rgba(226,232,240,0.9)] bg-white p-[29px] shadow-card">
        <header className="flex items-start justify-between border-b border-[#f1f5f9] pb-5">
          <div className="flex flex-col gap-1.5">
            <h2 className="text-base font-bold leading-4 text-[#0f172a]">Department Identity</h2>
            <p className="text-xs leading-4 text-[#64748b]">
              Specify core identifiers, territory hierarchy, and operational status.
            </p>
          </div>
          <StatusToggle active={values.active} onChange={(v) => setFieldValue('active', v)} />
        </header>

        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-5 md:flex-row">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <FieldLabel required>Department Name</FieldLabel>
              <input
                name="name"
                className={[
                  INPUT,
                  nameError ? '!border-red-400 focus:!border-red-500 focus:!ring-red-100' : '',
                ].join(' ')}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g. Revenue & Land Reforms Department"
              />
              {nameError && <span className="text-xs font-medium text-[#ba1a1a]">{nameError}</span>}
            </div>
            <div className="form-field-multiselect flex min-w-0 flex-1 flex-col gap-1.5">
              <FieldLabel>Territory Hierarchy</FieldLabel>
              <TreeSelect
                className="w-full"
                multiple
                treeCheckable
                showCheckedStrategy={TreeSelect.SHOW_PARENT}
                allowClear
                showSearch
                treeNodeFilterProp="title"
                treeNodeLabelProp="fullTitle"
                treeDefaultExpandAll
                loading={territoryLoading}
                disabled={territoryLoading}
                placeholder={territoryLoading ? 'Loading hierarchies…' : 'Select hierarchies'}
                notFoundContent={territoryLoading ? 'Loading…' : 'No hierarchies'}
                popupMatchSelectWidth={false}
                value={values.territory}
                onChange={(v: string[]) => setFieldValue('territory', v)}
                treeData={territoryTreeData}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5 pb-1.5">
            <FieldLabel>Description</FieldLabel>
            <textarea
              name="description"
              rows={3}
              className={[INPUT, 'resize-none leading-5'].join(' ')}
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>
        </div>
      </section>

      {/* Section: Designations & Rank */}
      <section className="flex flex-col gap-6 rounded-xl border border-[rgba(226,232,240,0.9)] bg-white p-[29px] shadow-card">
        <header className="flex items-center justify-between border-b border-[#f1f5f9] pb-5">
          <div className="flex flex-col gap-1.5">
            <h2 className="text-base font-bold leading-4 text-ink">Designations &amp; Rank</h2>
            <p className="text-xs leading-4 text-[#64748b]">
              Add designations, assign numeric ranks (1 = highest seniority), authority, and set
              escalation flow.
            </p>
          </div>
          <button
            type="button"
            onClick={openAddModal}
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-[#d1e2ff] bg-brand-shade px-[13px] py-[7px] text-xs font-semibold text-brand transition-colors hover:bg-[#e0ebff]"
          >
            <PlusIcon className="size-3.5" />
            Add Designation
          </button>
        </header>

        {/* Hierarchy table */}
        <div className="overflow-x-auto rounded-lg border border-line-soft">
          <div className="min-w-[840px]">
            {/* Header row */}
            <div className={[GRID, 'bg-[#f8fafc]'].join(' ')}>
              {(
                [
                  ['RANK', 'justify-start'],
                  ['DESIGNATION NAME', 'justify-start pl-8'],
                  ['AUTHORITY', 'justify-center text-center'],
                  ['LOWEST LEVEL?', 'justify-center text-center'],
                  ['ESCALATES TO', 'justify-start'],
                  ['ACTIONS', 'justify-end text-right'],
                ] as const
              ).map(([label, align]) => (
                <div key={label} className={['flex px-4 py-3', align].join(' ')}>
                  <span className="text-xs font-bold uppercase tracking-[0.6px] text-ink">{label}</span>
                </div>
              ))}
            </div>

            {/* Body rows — escalation points to the next-senior post: the designation with the
                nearest STRICTLY smaller rank. Posts sharing a rank all escalate to the same
                higher post (e.g. two Rank-4 posts both escalate to the Rank-3 post). */}
            {sorted.map((row) => {
              const seniorRank = Math.max(
                ...sorted.filter((r) => r.rank < row.rank).map((r) => r.rank),
                -Infinity,
              );
              const senior = Number.isFinite(seniorRank)
                ? sorted.find((r) => r.rank === seniorRank) ?? null
                : null;
              return (
                <DesignationRow
                  key={row.id}
                  row={row}
                  escalatesTo={senior ? `${baseName(senior.name)} (Rank ${senior.rank})` : null}
                  onEdit={() => openEditModal(row.id)}
                  onDelete={() => removeDesignation(row.id)}
                />
              );
            })}
          </div>
        </div>

        {/* Helper callout */}
        <div className="flex items-start gap-2.5 rounded-lg border border-[#dbeafe] bg-[rgba(239,246,255,0.7)] p-[15px]">
          <InfoCircleIcon className="mt-0.5 size-4 shrink-0 text-brand" />
          <p className="text-xs leading-[19.5px] text-[#475569]">
            <span className="font-semibold text-ink">Rank Rule Note:</span> Smaller rank numbers
            indicate higher seniority (Rank 1 is highest). Escalation automatically moves from the
            assigned officer to the next officer of a smaller rank number.
          </p>
        </div>
      </section>

      {/* Action footer */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-lg border border-[#cbd5e1] bg-white px-[25px] py-[11px] text-sm font-medium text-[#334155] transition-colors hover:bg-brand-shade"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => formik.submitForm()}
          disabled={saving}
          className="rounded-lg bg-brand px-6 py-2.5 text-sm font-semibold text-white shadow-card transition-colors hover:bg-brand-dark disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save & Proceed'}
        </button>
      </div>

      <AddDesignationModal
        open={modalOpen}
        defaultRank={nextRank}
        initial={editingRow}
        onCancel={() => {
          setModalOpen(false);
          setEditingId(null);
        }}
        onAdd={submitDesignation}
      />
    </div>
  );
}
